import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TEST_IDS } from "@/constants/testIds";
import { cn } from "@/lib";
import { useAuthStore } from "@/store";
import { FileText, Loader2, Paperclip, Pencil } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddTextContentDialog from "../../../dialogs/AddTextContentDialog";
import SketchDialog from "../../../dialogs/SketchDialog";
import { GoogleDriveIcon } from "../../header/icons/GoogleDriveIcon";
import { ACCEPTED_FILE_TYPES } from "../constants/fileConstants";
import GoogleDriveButton from "./GoogleDriveButton";

const FileUploadButton = ({
  onClick,
  disabled,
  isUploading,
  className,
  isLimitedMode = false,
  disableFileUpload = false,
  onShowAuthOverlay,
}) => {
  const [isGoogleDriveLoading, setIsGoogleDriveLoading] = useState(false);
  const [googleDriveError, setGoogleDriveError] = useState(null);
  const [googleDriveAvailable, setGoogleDriveAvailable] = useState(false);
  const googleDriveButtonRef = useRef(null);
  const { user, profile } = useAuthStore();
  const navigate = useNavigate();
  const [isAddTextDialogOpen, setIsAddTextDialogOpen] = useState(false);
  const [isSketchDialogOpen, setIsSketchDialogOpen] = useState(false);

  useEffect(() => {
    const checkGoogleDriveAvailability = () => {
      // 1. Check environment credentials
      const hasCredentials = !!(
        import.meta.env.VITE_GOOGLE_API_KEY &&
        import.meta.env.VITE_GOOGLE_CLIENT_ID
      );

      if (!hasCredentials) {
        setGoogleDriveAvailable(false);
        return;
      }

      // 2. Check beta feature flag
      // For unauthenticated users, default to false
      // For authenticated users, check their beta settings
      const betaEnabled = profile?.settings?.beta?.googleDriveAccess || false;

      if (!betaEnabled) {
        setGoogleDriveAvailable(false);
        return;
      }

      // 3. All checks passed
      setGoogleDriveAvailable(true);
    };

    checkGoogleDriveAvailability();
  }, [profile]);

  const handleLocalFileSelect = (e) => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = Object.entries(ACCEPTED_FILE_TYPES)
      .map(([mime, exts]) =>
        exts.map((ext) => (mime.includes("*") ? `.${ext}` : mime))
      )
      .flat()
      .join(",");

    fileInput.onchange = (event) => {
      if (event.target.files && event.target.files.length > 0) {
        onClick(event);
      }
    };

    fileInput.click();
  };

  const handleGoogleDriveFileSelect = (files) => {
    setIsGoogleDriveLoading(false);
    setGoogleDriveError(null);

    if (files && files.length > 0) {
      const syntheticEvent = {
        target: {
          files: null,
          googleDriveFiles: files,
        },
        preventDefault: () => {},
        stopPropagation: () => {},
        isGoogleDrive: true,
      };

      onClick(syntheticEvent);
    }
  };

  const handleGoogleDriveError = (error) => {
    setIsGoogleDriveLoading(false);

    let userMessage = "Google Drive is temporarily unavailable";

    if (error?.includes("not configured")) {
      userMessage = "Google Drive integration not configured";
    } else if (error?.includes("timeout") || error?.includes("loading")) {
      userMessage =
        "Google Drive is taking too long to load. Please try again.";
    } else if (error?.includes("network") || error?.includes("script")) {
      userMessage =
        "Unable to connect to Google Drive. Please check your connection.";
    } else if (
      error?.includes("Initialization in progress") ||
      error?.includes("still loading")
    ) {
      userMessage =
        "Google Drive is starting up. Please wait a moment and try again.";
    } else if (
      error?.includes("Services not ready") ||
      error?.includes("not ready")
    ) {
      userMessage =
        "Google Drive is not ready yet. Please try again in a few seconds.";
    }

    setGoogleDriveError(userMessage);
    console.error("Google Drive error:", error);
  };

  const handleGoogleDriveClick = () => {
    setGoogleDriveError(null);
    setIsGoogleDriveLoading(true);

    if (googleDriveButtonRef.current?.openPicker) {
      try {
        googleDriveButtonRef.current.openPicker();
      } catch (error) {
        console.error("Error calling openPicker:", error);
        setIsGoogleDriveLoading(false);
        setGoogleDriveError("Failed to open Google Drive picker");
      }
    } else {
      setIsGoogleDriveLoading(false);
      setGoogleDriveError("Google Drive picker not ready");
    }
  };

  if (!user && isLimitedMode) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8 p-1 rounded-md",
                "bg-transparent",
                "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                "transition-all duration-200",
                "text-zinc-700 dark:text-zinc-300",
                "focus:outline-none focus:ring-2 focus:ring-zinc-500/30",
                "disabled:opacity-50 disabled:hover:bg-transparent",
                className
              )}
              aria-label="Attach file"
              disabled={disabled || isUploading}
              onClick={() => {
                onShowAuthOverlay && onShowAuthOverlay("File Upload");
              }}
            >
              <Paperclip className="icon h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>Upload photos and files</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <DropdownMenu modal={false}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild disabled={disabled || isUploading}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                data-testid={TEST_IDS.FILE_UPLOAD_BUTTON}
                className={cn(
                  "h-8 w-8 p-1 rounded-md",
                  "bg-transparent",
                  "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                  "transition-all duration-200",
                  "text-zinc-700 dark:text-zinc-300",
                  "focus:outline-none focus:ring-2 focus:ring-zinc-500/30",
                  "disabled:opacity-50 disabled:hover:bg-transparent",
                  className
                )}
                aria-label="Attach file"
                disabled={disabled || isUploading}
              >
                <Paperclip className="icon h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>Upload photos and files</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent
          align="start"
          side="bottom"
          className="w-[180px] p-0 z-[400] rounded-xl border border-gray-200/70 dark:border-zinc-600/50 bg-white/70 dark:bg-zinc-800/70 backdrop-blur-md shadow-lg shadow-black/10"
          sideOffset={8}
          style={{ zIndex: 400 }}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {!user && isLimitedMode ? (
            <div className="w-48 p-3 text-center">
              <p className="text-xs text-muted-foreground mb-2">
                Sign in to upload files
              </p>
              <Button
                onClick={() =>
                  onShowAuthOverlay && onShowAuthOverlay("File Upload")
                }
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-7 rounded-lg"
                size="sm"
              >
                Sign In
              </Button>
            </div>
          ) : (
            <>
              <DropdownMenuItem
                className="flex items-center text-gray-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700/70 focus:bg-zinc-100 dark:focus:bg-zinc-700/70 cursor-pointer"
                onSelect={handleLocalFileSelect}
                data-testid={TEST_IDS.FILE_UPLOAD_LOCAL}
              >
                <Paperclip className="h-4 w-4 mr-2" />
                <span>Choose local files</span>
              </DropdownMenuItem>

              {googleDriveAvailable && (
                <DropdownMenuItem
                  className="flex items-center text-gray-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700/70 focus:bg-zinc-100 dark:focus:bg-zinc-700/70 cursor-pointer"
                  onSelect={handleGoogleDriveClick}
                  disabled={isGoogleDriveLoading}
                  data-testid={TEST_IDS.FILE_UPLOAD_GOOGLE_DRIVE}
                >
                  <GoogleDriveIcon className="h-4 w-4 mr-2" />
                  <span>
                    {isGoogleDriveLoading ? "Loading..." : "Choose drive files"}
                  </span>
                  {isGoogleDriveLoading && (
                    <Loader2 className="h-3 w-3 ml-2 animate-spin" />
                  )}
                </DropdownMenuItem>
              )}

              {user && !isLimitedMode && (
                <DropdownMenuItem
                  className="flex items-center text-gray-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700/70 focus:bg-zinc-100 dark:focus:bg-zinc-700/70 cursor-pointer"
                  onSelect={() => setIsAddTextDialogOpen(true)}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  <span>Add text content</span>
                </DropdownMenuItem>
              )}

              {user && !isLimitedMode && (
                <DropdownMenuItem
                  className="flex items-center text-gray-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700/70 focus:bg-zinc-100 dark:focus:bg-zinc-700/70 cursor-pointer"
                  onSelect={() => setIsSketchDialogOpen(true)}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  <span>Draw a sketch</span>
                </DropdownMenuItem>
              )}

              {googleDriveError && (
                <div className="px-3 py-2 text-xs text-red-600 dark:text-red-400 border-t border-border/40">
                  Error: {googleDriveError}
                </div>
              )}
            </>
          )}
        </DropdownMenuContent>

        <GoogleDriveButton
          ref={googleDriveButtonRef}
          id="google-drive-button"
          onFilesSelected={handleGoogleDriveFileSelect}
          onError={handleGoogleDriveError}
          disabled={disabled || isGoogleDriveLoading}
          className="hidden"
        >
          Google Drive
        </GoogleDriveButton>

        <AddTextContentDialog
          open={isAddTextDialogOpen}
          onOpenChange={setIsAddTextDialogOpen}
          onAddContent={(syntheticEvent) => {
            onClick(syntheticEvent);
          }}
        />

        <SketchDialog
          open={isSketchDialogOpen}
          onOpenChange={setIsSketchDialogOpen}
          onSaveSketch={(syntheticEvent) => {
            onClick(syntheticEvent);
          }}
        />
      </DropdownMenu>
    </TooltipProvider>
  );
};

FileUploadButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isUploading: PropTypes.bool,
  className: PropTypes.string,
  isLimitedMode: PropTypes.bool,
  disableFileUpload: PropTypes.bool,
  onShowAuthOverlay: PropTypes.func,
};

export default FileUploadButton;
